import axios from "axios";
import api from '../api/'
module.exports = {
    login: function (username, password, captcha, callback) {
        this.getToken(
            username,
            password,
            captcha,
            (response) => {
                this.updateLocalStorage(response.data);
                if (callback) callback({ "success": true });
            },
            (error) => {
                if (callback) callback(Object.assign({ "success": false }, error));
            })
    },

    /**
     * Segundo paso del login: canjea el desafio y el codigo por una sesion.
     *
     * El `desafio` lo entrego el POST /auth/ anterior, que para un usuario con
     * autenticador contesta 400 con meta.codigo = 'segundo_factor_requerido' y
     * sin token. O sea que mientras no pase por aca, en localStorage no hay
     * nada: esta funcion es la unica que completa el login de esos usuarios.
     *
     * No manda ningun header de autorizacion, y no es un olvido: el token de
     * sesion es justamente lo que este request crea. Lo que hace de credencial
     * es el desafio, que es de un solo uso y vence en minutos.
     */
    verificarSegundoFactor: function (desafio, codigo, callback) {
        var self = this;
        axios.post(api.authSegundoFactor, {
            desafio: desafio,
            codigo: codigo,
        }).then(function (response) {
            if (response.status == 200) {
                self.updateLocalStorage(response.data.data);
                if (callback) callback({ "success": true });
            } else {
                if (callback) callback({ "success": false });
            }
        }).catch(function (e) {
            // Mismo parseo que el login: el 400 puede ser "el codigo esta mal"
            // y el 429 el bloqueo por IP, y los dos tienen que poder mostrar su
            // propio mensaje.
            if (callback) callback(Object.assign({ "success": false }, self.parsearError(e)));
        });
    },

    sessionTimeoutLogin: function (token, password, callback) {
        var self = this;
        axios.post(api.authCheckWithToken, {
            token: token,
            password: password
        }, this.header(token)).then(response => {
            if (response.status == 200) {
                let data = response.data.data;
                this.updateLocalStorage(data);
                if (callback) callback({ "success": true });
            } else {
                if (callback) callback({ "success": false });
            }
        }).catch(function (e) {
            // Esta pantalla no tiene captcha (un solo fallo borra el token y
            // rebota al login, que si lo tiene), pero si puede recibir el 429
            // del bloqueo por IP y conviene decirlo en vez de mostrar "la
            // contrasenia es incorrecta".
            if (callback) callback(Object.assign({ "success": false }, self.parsearError(e)));
        });
    },

    /**
     * Pide un desafio nuevo sin gastar un intento (el boton de "otra imagen").
     *
     * El backend solo devuelve uno si para esa IP/usuario realmente hace falta;
     * si no hace falta contesta sin captcha y aca llega null.
     */
    pedirCaptcha: function (username, callback) {
        axios.post(api.authCaptcha, { username: username }).then(function (response) {
            let data = response.data.data || {};
            if (callback) callback(data.captcha || null);
        }).catch(function () {
            if (callback) callback(null);
        });
    },

    /**
     * Normaliza un error de axios al sobre {meta, data, errors} del backend.
     *
     * Hace falta porque los casos del login no se distinguen por el status: el
     * 400 puede ser "credenciales incorrectas", "resolve este captcha" o
     * "faltan los 6 digitos del autenticador", y el que los separa es
     * meta.codigo.
     */
    parsearError: function (e) {
        let respuesta = (e && e.response) || null;
        let cuerpo = (respuesta && respuesta.data) || {};
        let meta = cuerpo.meta || {};
        let data = cuerpo.data || {};
        let errors = cuerpo.errors || {};
        return {
            status: respuesta ? respuesta.status : 0,
            codigo: meta.codigo || null,
            captchaRequerido: !!meta.captchaRequerido,
            captcha: data.captcha || null,
            // El 400 de 'segundo_factor_requerido' es un login que SI acerto la
            // contraseña: el desafio es lo que hay que devolver junto con el
            // codigo en verificarSegundoFactor.
            segundoFactorRequerido: !!meta.segundoFactorRequerido,
            desafio: data.desafio || null,
            segundosRestantes: meta.segundosRestantes || 0,
            // errors es {'error': '<mensaje>'} en los casos de login. En los de
            // validacion del serializer es un dict por campo, que no se muestra
            // crudo: ahi mensaje queda null y el formulario usa su texto propio.
            mensaje: typeof errors.error === 'string' ? errors.error : null,
        };
    },

    logout: function () {
        // El endpoint ahora exige token valido: si ya vencio responde 401,
        // igual hay que limpiar la sesion local.
        let clear = function () {
            delete localStorage.token;
            delete localStorage.permisos;
            window.location.reload();
        };
        axios.get(
            api.authLogout,
            this.header()
        ).then(clear).catch(clear)
    },

    isLoggedIn: async function () {
        if (typeof (localStorage.token) != "undefined") {
            var response = await this.checkToken(localStorage.token);
            if (response.exists) {
                // Con await: el sidebar y los botones leen localStorage.permisos
                // de forma sincronica, y si esto no se espera renderizan con la
                // lista de la carga anterior. Como escribir en localStorage no
                // dispara un re-render, la pantalla queda mostrando permisos que
                // el usuario ya no tiene hasta la proxima recarga.
                await this.updatePermission();
                return response;
            } else {
                return false;
            }
        } else {
            return false;
        }
    },

    clearToken: function (callback) {
        axios.get(api.clearToken, this.header()).then(response => { if (callback) callback(response.data.data) });
        delete localStorage.token;
        delete localStorage.permisos;
    },

    checkToken: async function (token) {
        let reply = (await axios.post(api.authCheck, {
            token: token
        }));
        this.updateLocalStorage(reply.data.data);
        return reply.data.data;
    },



    hasPermission: function (permission) {
        if (localStorage.permisos != "" && localStorage.permisos != undefined) {
            var permisos = JSON.parse(localStorage.permisos);
            if (permisos.filter(x => x == permission).length > 0) {
                return true;
            } else {
                return false;
            }
        }
    },

    // Devuelve la promesa para que isLoggedIn pueda esperarla: sin eso, quien la
    // llama sigue de largo y la UI se arma con los permisos de la carga anterior.
    updatePermission: function () {
        var self = this;
        return axios.get(api.authGetPermissions, self.header()).then(function (response) {
            if (response.status == 200) {
                let data = response.data.data;
                localStorage.permisos = JSON.stringify(data.permisos);
            } else {
                self.logout();
            }
        }).catch(function () {
            // Si no se pudo refrescar, no dejar la lista vieja en uso: es peor
            // mostrar accesos que el usuario ya no tiene que no mostrar ninguno.
            localStorage.permisos = JSON.stringify([]);
        })
    },

    token: function () {
        return localStorage.token;
    },

    header: function (token) {
        return {
            headers: {
                "Authorization": "Token " + (token ? token : localStorage.token),
            },
        };
    },

    fileFormHeader: function (token) {
        return {
            headers: {
                "Authorization": "Token " + (token ? token : localStorage.token),
                "Content-Type": "multipart/form-data"
            },
            async: false,
            cache: false,
            contentType: false,
            processData: false,
        };
    },


    getToken: function (username, password, captcha, callback, errorCallback) {
        var self = this;
        let datos = {
            username: username,
            password: password,
        };
        // Solo se mandan si hay algo que mandar: el backend trata "no vino" y
        // "vino mal" como casos distintos (captcha_requerido vs
        // captcha_invalido) y el mensaje al usuario no es el mismo.
        if (captcha && captcha.key) {
            datos.captcha_key = captcha.key;
            datos.captcha_value = captcha.value || '';
        }
        axios.post(api.auth, datos).then(function (response) {
            if (response.status == 200) {
                if (callback) callback(response.data);
            } else {
                if (errorCallback) errorCallback({});
            }
        }).catch(function (e) {
            // Este catch descartaba la respuesta entera. Sin el status y el
            // cuerpo, el formulario no puede saber si hay que mostrar un
            // captcha ni cuanto falta para que se levante un bloqueo: todo
            // error se veia igual.
            if (errorCallback) errorCallback(self.parsearError(e));
        })
    },

    updateLocalStorage: function (data) {
        if (data.token != undefined) localStorage.token = data.token;
        if (data.permisos != undefined) localStorage.permisos = JSON.stringify(data.permisos);
        if (data.configuraciones != undefined) localStorage.configuraciones = JSON.stringify(data.configuraciones);
        if (data.displayname != undefined) localStorage.displayname = data.displayname;
        if (data.iduser != undefined) localStorage.iduser = data.iduser; 
    }
}


// WEBPACK FOOTER //
// ./src/auth/auth.js